import { z } from 'zod';
import { belegzustandSchema } from './documents.js';
import { lastschriftEinzugsstandSchema } from './lastschrift.js';
import { centAmount, isoDate, uuid } from './posting.js';
/**
 * Offene Posten und Zahlstatus — Plan Task 14 `[C-offeneposten]`
 * (`docs/superpowers/plans/2026-08-26-sachkonten-abgleich.md`) plus der
 * Auftraggeberwunsch vom 2026-08-28 („Zustand wie Unbezahlt, Bezahlt,
 * Mahnstatus etc. Und auch die Information mit was die Rechnung bezahlt wurde
 * — Guthaben, Überweisung, Mollie, PayPal").
 *
 * **Nichts davon wird gespeichert. Alles ist Ableitung aus dem Journal.**
 * Der Plan entscheidet das ausdrücklich: KEINE neue Spalte, KEIN Zustand
 * `bezahlt` in `document_status`. Migration `0019` führt drei Werte
 * (`draft`, `issued`, `cancelled`), und eine bezahlte Rechnung bleibt
 * `issued` — Kapitel 8.2 der Verfahrensdokumentation begründet das
 * ausführlich: Ein Beleg kann gleichzeitig versandt, teilweise bezahlt und
 * gemahnt sein, und eine Aufzählung mit einem Wert je Zeile kann das nicht.
 *
 * **Die Ableitung hat genau zwei Ebenen, und sie sind NICHT dasselbe.**
 *
 * 1. **Der offene Posten je Kunde** ist der Saldo des Forderungskontos
 *    (`document_posting_account`, `purpose = 'receivable'`, heute `1400`) über
 *    `journal_line.customer_id`. Er ist vollständig — jede Zeile des Kontos
 *    zählt mit, gleich woher sie kommt.
 * 2. **Der Zahlstatus je Rechnung** stellt die Sollzeile DIESER Rechnung
 *    ihren Zahlungen gegenüber. Er ist NICHT vollständig: Eine Zahlung, die
 *    keinen maschinellen Bezug zur Rechnung trägt, sieht er nicht.
 *
 * Die Differenz zwischen beiden steht als `nichtZugeordnet` im Bericht. Sie zu
 * verschweigen hiesse, die schwächere Zahl als die stärkere auszugeben.
 *
 * **Welche Zahlungen die Ableitung sehen kann** — der ehrliche Join, und er
 * hat genau drei Zweige:
 *
 * | Zahlungsart | Bezug zur Rechnung |
 * |---|---|
 * | Guthaben-Einlösung | `journal_entry.document_id` (`guthaben/einloesung.ts` setzt es) |
 * | Kontierte Geldbewegung | `bank_transaction.journal_entry_id` → dieselbe Zeile trägt `matched_document_id` |
 * | Gegenbuchung zu einer der beiden | `journal_entry.reverses_entry_id` |
 *
 * **Was sie NICHT sehen kann, benannt statt verschwiegen:**
 *
 * - Eine **Alt-Zahlung ohne Buchung** — vor dem Buchungspfad
 *   (`C-kontbuchung`, 2026-08-28) gelandet und nie gebucht — trägt keinen
 *   `journal_entry` und erscheint nirgends. Die Rechnung sieht unbezahlt aus,
 *   und sie ist es im Journal auch.
 * - Eine **kontierte Zahlung ohne Belegzuordnung** (`matched_document_id`
 *   leer) bucht auf das Forderungskonto und trägt den Kunden, aber keine
 *   Rechnung. Sie senkt den Kundensaldo und keinen Rechnungsstatus.
 * - Eine **Gutschrift** ist keine Zahlung. Sie mindert die Forderung des
 *   Kunden über ihre eigene Belegbuchung; der Zahlstatus der ursprünglichen
 *   Rechnung bleibt davon unberührt.
 *
 * **Ein Befund, gemessen und nicht behauptet** (`E-495`): Eine
 * **Guthaben-Einlösung lässt sich im Journal gar nicht gegenbuchen.** Ihre
 * Buchung trägt `document_id` auf die eingelöste Rechnung, und
 * `assert_journal_document_reversal` (Migration `0025`) verlangt für die
 * Gegenbuchung einer Belegbuchung einen freigegebenen **Stornobeleg** — den es
 * für eine Zahlung nicht gibt. Der Rückweg aus einer irrtümlichen Einlösung
 * ist heute nicht gebaut. Der `storno`-Zweig des Joins greift deshalb nur bei
 * kontierten Geldbewegungen, die unmittelbar im Journal gegengebucht werden;
 * die Rücknahme über `KontierungService.stornieren()` nimmt Urbuchung und
 * Gegenbuchung gemeinsam aus dem Bezug (in der Summe null) und braucht ihn
 * nicht.
 *
 * ---------------------------------------------------------------------------
 * NACHTRAG 2026-09-07 (`ZZ-offeneposten`): DIE OFFENE-POSTEN-SEITE LIEST
 * SEITHER `beleg_offener_rest`, DER ZAHLSTATUS JE RECHNUNG NICHT
 * ---------------------------------------------------------------------------
 *
 * Alles oben Geschriebene gilt weiter für `GET /berichte/zahlstatus` und
 * `GET /berichte/zahlstatus/:documentId` — die Spalte der Belegliste und die
 * Teilzahlungsliste der Belegansicht. Für `GET /berichte/offene-posten` und
 * die Startkennzahl «Offene Forderungen» ist die Quelle seit dem 2026-09-07
 * die Sicht `beleg_offener_rest` (`0161`, fortgeschrieben von `0174`), also
 * dieselbe, an der auch der Zuordnungsweg und das Zahlungsstand-Abzeichen
 * hängen.
 *
 * **Der Anlass ist gemessen und nicht vermutet.** Die alte Auswahl
 * `document.status = 'issued' AND document.type = ANY(<invoice,
 * partial_invoice, final_invoice, recurring_invoice>)` traf auf dem
 * Staging-Bestand am 2026-09-07 **null** Belege — der ganze Forderungsbestand
 * steht dort als `legacy_invoice`. Die Seite zeigte deshalb je Kunde
 * «Aus Rechnungen 0,00», «Offene Rechnungen 0», «Älteste offene Rechnung —»
 * und den vollen Kontosaldo unter «Nicht zugeordnet»; gleichzeitig führte
 * `beleg_offener_rest` 136 offene gebuchte Forderungen über 29.744,93 € und
 * `zahlung_klaerung` 1.882 wartende Geldeingänge.
 *
 * **Was sich dadurch an den drei Zahlen je Kunde ändert.** `saldo` bleibt
 * der Kontostand aus `journal_line.customer_id` und ist unverändert.
 * `offenAusRechnungen` ist jetzt die Summe der offenen RESTE aus der Sicht —
 * über ALLE Belegarten mit Forderung, `legacy_invoice` eingeschlossen — und
 * zählt beide Tilgungswege mit: die freigegebene Zahlungszuordnung
 * (`zahlung_zuordnung_position`, Art `hauptforderung`) und die
 * journalseitige Tilgung (`eingeloest`, vor allem die Guthaben-Einlösung).
 * `nichtZugeordnet` bleibt ihre Differenz und wird damit zum ersten Mal
 * aussagekräftig.
 *
 * **Was die Seite NICHT tut, und das ist der Kern der Auftraggeberfrage vom
 * 2026-09-07 („Ich kann immer noch nicht über die UI Rechnungen … auf bezahlt
 * buchen"):** Sie hat keinen Schalter «bezahlt». Bezahlt wird ein Beleg
 * durch eine zugeordnete und freigegebene Zahlung (Zahlungsklärung →
 * Zuordnen → Zahlungsfreigabe) oder durch Guthabenverrechnung. Ein
 * Zustandsfeld daneben wäre die zweite Wahrheit, die `D-15` und `D-16`
 * ausdrücklich verhindern.
 *
 * **Die Überfälligkeit wird ANGEZEIGT und nicht betrieben.** Die Stufen
 * +3/+10/+20/+30 Tage stammen aus der Mahnwesen-Spec
 * (`docs/superpowers/specs/2026-08-19-mahnwesen-design.md`, 3.1) und stehen
 * hier ausschliesslich als **Vorschau**: „Stufe wäre N". Es entsteht kein
 * Mahnbeleg, keine Gebührenbuchung, kein `dunning_case` und kein Zinslauf —
 * das Mahnwesen ist eine eigene Phase mit eigener Spec, und eine halbe
 * Umsetzung davon wäre schlimmer als keine.
 *
 * ---------------------------------------------------------------------------
 * NACHTRAG 2026-09-08 (`K-altbelegeueberall`, `D-31`): DER ZAHLSTATUS KENNT
 * DIE GEBUCHTE ALTKLASSE
 * ---------------------------------------------------------------------------
 *
 * Auftraggeberentscheid vom 2026-09-08: «Überall. Die Altrechnungen sind ja
 * genauso Teil unserer Buchhaltung. Es muss ja alles stimmen auch mit
 * Altrechnungen.»
 *
 * `GET /berichte/zahlstatus` und `GET /berichte/zahlstatus/:documentId`
 * führen seither `legacy_invoice` mit. Die Zahlungsspalte der Belegliste und
 * der Zahlungsblock der Belegansicht standen an einer importierten Rechnung
 * ab 2026 bis dahin leer, obwohl dieses System für sie eine Forderung auf
 * dem Forderungskonto gebucht hat und Zahlungen darauf zuordnet — dieselbe
 * Fehlerklasse wie die Offene-Posten-Seite vom Vortag, nur eine Ebene
 * weiter.
 *
 * **Die Zusage ist die gebuchte Klasse und ausdrücklich nicht die
 * Archivklasse.** Beide tragen dieselbe Belegart; getrennt werden sie an der
 * Forderungsbuchung (`D-1` der Altimport-Spec). Ein Archivbeleg ohne
 * Journaleintrag hätte `forderung = 0,00` und `bezahlt = 0,00` und hiesse
 * nach dem ersten Zweig von `zahlstatusAus()` **«bezahlt»** — in der Liste
 * stünde an jeder unbezahlten Altrechnung vor 2026 das Wort «Bezahlt». Die
 * Abfrage hält die Archivklasse deshalb zusätzlich gegen die vorhandene
 * Forderungsbuchung (`ist_archivbelegtyp()`, Migration `0119`).
 *
 * Was die Archivklasse über ihren Zahlungsstand sagen kann, sagt sie über
 * `document.legacy_status` und den vereinten Belegzustand — nicht über eine
 * Ableitung aus einem Journal, in dem sie nie steht.
 *
 * **`legacy_cancellation` und `legacy_credit_note` bleiben draussen**, aus
 * denselben Gründen wie `cancellation` und `credit_note`: Eine Gutschrift ist
 * eine Verbindlichkeit gegenüber dem Kunden, ein Storno kehrt um statt zu
 * tilgen.
 */
/**
 * Der abgeleitete Zahlstatus einer Ausgangsrechnung.
 *
 * Drei Werte und nicht vier: „überfällig" ist KEIN Zahlstatus, sondern eine
 * Aussage über das Datum. Eine überfällige Rechnung ist `offen` oder
 * `teilbezahlt`; wäre „überfällig" ein vierter Wert, verlöre man die Auskunft,
 * ob schon etwas bezahlt wurde.
 */
export const zahlstatusSchema = z.enum(['offen', 'teilbezahlt', 'bezahlt']);
/**
 * Womit bezahlt wurde.
 *
 * `guthaben` kommt aus dem Quellsystem der Buchung (`guthaben.einloesung`);
 * die drei Geldwege kommen aus `bank_account.provider` der zahlenden Bewegung
 * — `fints` heisst für einen Menschen **Überweisung** und nicht „FinTS": Der
 * Kanal, über den ein Auszug abgeholt wurde, ist nicht der Weg, auf dem das
 * Geld kam.
 *
 * `storno` ist die Gegenbuchung zu einer der vorstehenden. Sie steht als
 * eigener Wert und nicht als negativer Betrag eines der anderen Wege, weil
 * eine Teilzahlungsliste sonst behauptete, es habe eine Zahlung „über minus
 * 119,00 € per PayPal" gegeben.
 *
 * `unbekannt` bleibt für einen künftigen `bank_provider`-Wert, den diese Datei
 * noch nicht kennt — eine Zuordnung zu raten wäre schlechter, als sie
 * offenzulassen.
 */
export const zahlwegSchema = z.enum([
    'guthaben',
    'ueberweisung',
    'paypal',
    'mollie',
    'storno',
    'unbekannt',
]);
/**
 * Eine einzelne Zahlung auf eine Rechnung — eine Journalbuchung, die das
 * Forderungskonto im Haben berührt und deren Bezug zur Rechnung sich
 * maschinell herstellen lässt.
 *
 * `betrag` ist vorzeichenbehaftet: Eine Gegenbuchung oder eine Rücklastschrift
 * trägt einen negativen Betrag. Ein Betrag ohne Vorzeichen zwänge die Anzeige,
 * die Richtung aus dem `zahlweg` zu erraten.
 */
export const rechnungZahlungSchema = z
    .object({
    journalEntryId: uuid,
    /** Die laufende Nummer der Buchung — die Anschrift im Journal, die ein Prüfer nennt. */
    entryNo: z.string(),
    bookingDate: isoDate,
    betrag: centAmount,
    zahlweg: zahlwegSchema,
    /** Die zahlende Geldbewegung, wenn es eine gab. `null` bei Guthaben. */
    bankTransactionId: uuid.nullable(),
    beschreibung: z.string(),
})
    .strict();
/**
 * Der Zahlstand einer Rechnung OHNE ihre Einzelzahlungen — die Form, in der
 * die Belegliste ihn spaltenweise zeigt.
 *
 * `forderung` ist der SOLL-Betrag auf dem Forderungskonto aus der Belegbuchung
 * dieser Rechnung und nicht `document.gross_total`. Beide stimmen im Regelfall
 * überein; wo sie es nicht tun, ist das Journal die Wahrheit — und ein
 * Bericht, der stattdessen den Belegkopf zeigt, verdeckte genau diesen Fall.
 */
export const rechnungZahlstandSchema = z
    .object({
    documentId: uuid,
    number: z.string().nullable(),
    customerId: uuid,
    customerName: z.string(),
    /** Die Debitorennummer des Kunden — die Anschrift, unter der ein Steuerberater sucht. */
    debtorNo: z.string(),
    issueDate: isoDate.nullable(),
    dueDate: isoDate.nullable(),
    forderung: centAmount,
    bezahlt: centAmount,
    offen: centAmount,
    status: zahlstatusSchema,
    /**
     * Tage seit dem Zahlungsziel, oder `null` — wenn kein `dueDate` gesetzt
     * ist, wenn es noch nicht überschritten ist, oder wenn nichts mehr offen
     * ist.
     *
     * Eine Rechnung ohne Zahlungsziel ist NICHT überfällig: § 286 BGB knüpft
     * den Verzug an eine Fälligkeit, und wo keine steht, wird hier auch keine
     * erfunden.
     */
    tageUeberfaellig: z.number().int().nonnegative().nullable(),
    /**
     * Die Mahnstufe, die zu `tageUeberfaellig` gehören WÜRDE — 0 bis 4, wobei
     * 0 „noch keine" heisst. Reine Anzeige (siehe Kopfkommentar der Datei); es
     * gibt keinen Mahnlauf, der sie setzt, und keinen Beleg, der sie trägt.
     */
    mahnstufeVorschau: z.number().int().min(0).max(4),
    /**
     * `true`, wenn dieser Beleg eine **Rücklastschrift nach der
     * Zwischenregel** trägt — `ZZ-zahlstatusrueck` (2026-09-30), `E-1714`,
     * Mehrkunden-Spec Abschnitt 10.4.
     *
     * Bis die Folgeära `LS-ruecklastschrift` die Rücklastschrift verbucht,
     * bleibt die Gutschrift der Bank zugeordnet, der Beleg gilt als getilgt
     * und der Einzug ist nur als `zurueckgegeben` markiert. Wahr ist das Feld
     * genau dann, wenn ein Einzug dieses Belegs `zurueckgegeben` ist, der
     * Beleg als erledigt gilt und eine Zuordnung aus einer **Geldbewegung**
     * den Einzug deckt — freigegeben NACH `eingereicht_am` und VOR
     * `zurueckgegeben_am`, nicht aufgehoben, nicht ersetzt. Falsch ist es für
     * die Rückgabe VOR der Gutschrift (der Beleg ist dann offen und wird
     * normal behandelt) und für eine Rückgabe, nach der der Kunde anders
     * zahlte oder ein späterer Einzug gelang.
     *
     * **Der Server liefert das Urteil, die Oberfläche zeigt es.** Sie kennt
     * weder die Reihenfolge der Freigabe noch die Quelle der Zuordnung und
     * darf die Lage nicht aus `status` und `lastschrift.einzug` erraten — bis
     * 2026-09-30 tat sie das, und ein Beleg, der nach einer frühen Rückgabe
     * anders bezahlt wurde, trug dasselbe Etikett fälschlich. Die eine
     * Bedingung steht in `apps/api/src/zahlungen/ruecklastschrift.ts`;
     * `pnpm zahlung:offen` nennt dieselben Fälle als Auskunft.
     */
    ruecklastschriftOffen: z.boolean(),
    /**
     * Der Lastschriftstand dieser Rechnung — Lastschrift-Spec, Abschnitt 5
     * (`E-1353`). `mandat` und `mandatsreferenz` kommen aus `sepa_mandat`
     * (höchstens ein aktives Mandat je Kunde), `einzug`/`einzugId` aus dem
     * jüngsten NICHT stornierten `lastschrift_einzug` dieses Belegs —
     * `einzug: 'keiner'`/`einzugId: null`, wenn keiner existiert oder nur
     * ein stornierter. Beides in DERSELBEN Abfrage wie der übrige
     * Zahlstand, kein N+1.
     *
     * **`einzugId` trägt Aufgabe `LS-belegknoepfe` nach**: Ohne sie kannte
     * die Belegseite keinen Einzug, den ihre Knöpfe
     * („Vormerkung stornieren", „Als zurückgegeben markieren") ansprechen
     * könnten — beide Routen (`POST /lastschrift/einzuege/:id/stornieren`
     * bzw. `…/zurueckgegeben`) verlangen die Einzugskennung im Pfad.
     */
    lastschrift: z
        .object({
        mandat: z.boolean(),
        mandatsreferenz: z.string().nullable(),
        einzug: lastschriftEinzugsstandSchema,
        einzugId: uuid.nullable(),
    })
        .strict(),
})
    .strict();
/** Der Zahlstand samt Teilzahlungsliste — die Form der Belegdetailansicht. */
export const rechnungZahlstatusSchema = rechnungZahlstandSchema
    .extend({ zahlungen: z.array(rechnungZahlungSchema) })
    .strict();
/** `GET /api/v1/berichte/zahlstatus/:documentId`. `null`, wenn der Beleg keine Ausgangsrechnung ist. */
export const zahlstatusAntwortSchema = z
    .object({ zahlstatus: rechnungZahlstatusSchema.nullable() })
    .strict();
/** `GET /api/v1/berichte/zahlstatus` — der Zahlstand aller ausgestellten Rechnungen. */
export const zahlstatusListeSchema = z
    .object({ rechnungen: z.array(rechnungZahlstandSchema) })
    .strict();
/**
 * Ein einzelner offener Beleg eines Kunden — Auftrag `ZZ-offeneposten`
 * (2026-09-07).
 *
 * **Die Quelle ist `beleg_offener_rest` (`0161`, fortgeschrieben von `0174`)
 * und nicht mehr die Belegtabelle.** Bis zu diesem Auftrag las
 * `offene-posten.query.ts` `document` mit
 * `status = 'issued' AND type = ANY(<die vier neuen Rechnungsarten>)`. Auf dem
 * Staging-Bestand traf diese Bedingung am 2026-09-07 **null** Belege: Der
 * gesamte Forderungsbestand steht dort als `legacy_invoice` (gebuchte Klasse
 * des Altimports, Ära Cutover), und die Liste zeigte deshalb je Kunde
 * «Aus Rechnungen 0,00», den vollen Saldo unter «Nicht zugeordnet» und
 * «Offene Rechnungen 0» — bei 32.555,78 € Kontosaldo. Die Sicht kennt
 * dagegen genau eine Klassengrenze (`ist_forderungsbeleg()`), und
 * `legacy_invoice` steht ausdrücklich darin.
 *
 * **`offen` ist der offene REST der Sicht** — Bruttobetrag minus der Summe
 * der freigegebenen, ungeersetzten Zuordnungspositionen der Art
 * `hauptforderung` (`zugeordnet`) minus der journalseitigen Tilgung auf dem
 * Forderungskonto ohne die eigene Ausstellungsbuchung (`eingeloest`, vor
 * allem die Guthaben-Einlösung `1796 an 1400`). Beide Summanden stehen
 * daneben, damit ein Leser den Rest nachrechnen kann, ohne die Sicht zu
 * kennen.
 *
 * **`belegzustand` ist der VEREINTE Zustand aus der Belegliste**
 * (`belegzustandAus()` in `documents.ts`, Auftrag `AI-zustandvereint`) und
 * nicht der Zahlstand dieser Zeile. Ein Archivbeleg, den das Altsystem als
 * bezahlt führt, steht hier als «Bezahlt» und trägt trotzdem einen offenen
 * Rest — das ist kein Widerspruch, sondern der Grund, warum beide Angaben
 * nebeneinander stehen: der Zustand sagt, was das Altsystem wusste, der Rest
 * sagt, was DIESES System gebucht hat.
 */
export const offenePostenBelegSchema = z
    .object({
    documentId: uuid,
    number: z.string().nullable(),
    /** Die Belegart, roh — `legacy_invoice` für die gebuchte Klasse des Altimports. */
    type: z.string(),
    belegzustand: belegzustandSchema,
    issueDate: isoDate.nullable(),
    dueDate: isoDate.nullable(),
    /** `document.gross_total` — die Forderung, wie sie ausgestellt wurde. */
    bruttobetrag: centAmount,
    /** Summe der freigegebenen, ungeersetzten Zuordnungen der Art `hauptforderung`. */
    zugeordnet: centAmount,
    /** Journalseitige Tilgung auf dem Forderungskonto ohne die eigene Ausstellungsbuchung. */
    eingeloest: centAmount,
    /** Der offene Rest: `bruttobetrag − zugeordnet − eingeloest`. */
    offen: centAmount,
    /** `beleg_offener_rest.ueberzahlt` — der Rest liegt unter null. */
    ueberzahlt: z.boolean(),
    status: zahlstatusSchema,
    tageUeberfaellig: z.number().int().nonnegative().nullable(),
    mahnstufeVorschau: z.number().int().min(0).max(4),
})
    .strict();
/** Die älteste noch offene Rechnung eines Kunden — der Griff, mit dem eine Mahnung anfinge. */
export const offenePostenAeltesteSchema = z
    .object({
    documentId: uuid,
    number: z.string().nullable(),
    issueDate: isoDate.nullable(),
    dueDate: isoDate.nullable(),
    offen: centAmount,
    tageUeberfaellig: z.number().int().nonnegative().nullable(),
    /**
     * Dieselbe Vorschau wie auf der Rechnungszeile — sie steht auch hier,
     * damit die Anzeige sie nicht ein zweites Mal ausrechnen muss. Eine zweite
     * Fristliste im Browser wäre genau die Dopplung, die veraltet, sobald das
     * Mahnwesen seine Konfigurationstabelle bekommt.
     */
    mahnstufeVorschau: z.number().int().min(0).max(4),
})
    .strict();
/**
 * Ein Kunde mit offenem Posten.
 *
 * **`saldo` und `offenAusRechnungen` sind zwei verschiedene Zahlen**, und
 * genau deshalb stehen beide da. `saldo` ist der Kontostand — vollständig, aus
 * dem Journal, unbestreitbar. `offenAusRechnungen` ist die Summe der
 * abgeleiteten Rechnungsstände; sie sieht nur, was einen Rechnungsbezug hat.
 * `nichtZugeordnet` ist ihre Differenz und benennt damit den Teil des Saldos,
 * den keine einzelne Rechnung erklärt: eine Zahlung ohne Belegzuordnung, eine
 * Gutschrift, eine Buchung von Hand.
 */
export const offenePostenKundeSchema = z
    .object({
    customerId: uuid,
    customerName: z.string(),
    debtorNo: z.string(),
    saldo: centAmount,
    offenAusRechnungen: centAmount,
    nichtZugeordnet: centAmount,
    /** Wie viele Forderungsbelege dieses Kunden einen offenen Rest tragen. */
    anzahlOffen: z.number().int().nonnegative(),
    /** Wie viele davon ihr Zahlungsziel überschritten haben. */
    anzahlUeberfaellig: z.number().int().nonnegative(),
    aelteste: offenePostenAeltesteSchema.nullable(),
    /**
     * Die offenen Belege selbst — seit `ZZ-offeneposten` (2026-09-07), damit
     * die Seite je Kunde aufklappen kann, wofür der Saldo steht.
     *
     * **Kein zweiter Abruf und keine zweite Ableitung.** Die Liste ist
     * dieselbe, aus der `offenAusRechnungen`, `anzahlOffen`,
     * `anzahlUeberfaellig` und `aelteste` gerechnet werden; sie mitzuliefern
     * kostet einen Durchlauf weniger als ein eigener Endpunkt und macht die
     * vier Zahlen darüber nachprüfbar.
     */
    belege: z.array(offenePostenBelegSchema),
    /**
     * Wie viele Klärfälle (`zahlung_klaerung`, `0161`) sich diesem Kunden
     * zuordnen lassen — Geldeingänge ohne geltende Zuordnung.
     *
     * **Die benannte Grenze:** Ein Klärfall trägt nur dann einen Kunden,
     * wenn im Verwendungszweck GENAU EIN Forderungsbeleg getroffen wurde
     * (`treffer = 1`). `kein_treffer` und `mehrdeutig` tragen keinen und
     * zählen deshalb bei keinem Kunden mit; sie stehen in
     * `klaerfaelleGesamt` des Berichts. Gemessen am 2026-09-07 auf Staging:
     * 286 von 1.882 Klärfällen sind einem Kunden zuzuordnen.
     */
    klaerfaelle: z.number().int().nonnegative(),
})
    .strict();
/**
 * Die Gesamtzahl überfälliger Rechnungen samt ihrer Summe, über **alle**
 * Kunden dieses Berichts — die Tageskontext-Zeile des Dashboards
 * (`docs/scribbles/2026-08-31-dashboard-design.md` Abschnitt 2a, „Überfällige
 * Rechnungen, eine Gesamtzahl fürs Dashboard"; Auftrag `B-dashueberfaellig`).
 *
 * **Kein neuer Endpunkt.** Das Design empfiehlt ausdrücklich ein billiges
 * Zusatzfeld an dieser bestehenden Antwort statt einer eigenen Aggregation —
 * `bericht()` zählt beim Aufbau von `kunden` ohnehin bereits
 * `anzahlUeberfaellig` je Kunde; dieses Feld ist nur die Summe darüber, in
 * derselben Schleife mitgeführt, kein zweiter Durchlauf.
 */
export const offenePostenUeberfaelligSchema = z
    .object({
    /** Wie viele ausgestellte Rechnungen, über alle Kunden, ihr Zahlungsziel überschritten haben. */
    anzahl: z.number().int().nonnegative(),
    /** Die Summe ihrer offenen Beträge — nicht die Summe der Rechnungsbeträge. */
    summe: centAmount,
})
    .strict();
/** `GET /api/v1/berichte/offene-posten` — die Offene-Posten-Liste je Kunde. */
export const offenePostenBerichtSchema = z
    .object({
    stichtag: isoDate,
    /** Die Nummer des Forderungskontos, über das gerechnet wurde — `1400`, aber nicht hartkodiert. */
    forderungskonto: z.string(),
    summe: centAmount,
    /**
     * Die Rechnungen aus einem Vorsystem (Lexware Office) vor ihrer
     * Eröffnungsbuchung, nach ihrem Zahlstand dort (`E-2080`, `D-86` der
     * Lexware-Spec): offen mit dem offenen Betrag zum Stichtag, solange er
     * nicht festgehalten ist mit dem Bruttobetrag. Sie stehen NICHT in
     * `summe` und nicht in `kunden` — beide lesen die gebuchte Forderung.
     */
    vorsystemOffen: z
        .object({
        summe: centAmount,
        anzahl: z.number().int().nonnegative(),
    })
        .strict(),
    /**
     * Die Zahl der Startseiten-Kachel „Offene Posten": `summe` plus
     * `vorsystemOffen.summe`, auf dem Server gerechnet. Beide Teile sind
     * disjunkt — eine Rechnung aus einem Vorsystem zählt entweder nach ihrem
     * Zahlstand dort oder, ab ihrer Eröffnungsbuchung, im Saldo.
     */
    summeMitVorsystem: centAmount,
    kunden: z.array(offenePostenKundeSchema),
    /** Die benannten Grenzen der Ableitung, im Bericht selbst. */
    grenzen: z.array(z.string()),
    ueberfaellig: offenePostenUeberfaelligSchema,
    /**
     * Alle Klärfälle aus `zahlung_klaerung`, auch die ohne ableitbaren
     * Kunden — die Summe über `klaerfaelle` je Kunde ist KLEINER als diese
     * Zahl und darf es sein (siehe die Grenze dort).
     */
    klaerfaelleGesamt: z.number().int().nonnegative(),
})
    .strict();
//# sourceMappingURL=offene-posten.js.map